// Words of the subscription indicator and banner (docs/tasks/36-subscription-status.md). The state comes from the
// server; these only turn it into short Persian text. «پلن پایه / آزمایشی»، «پرداخت در انتظار» and «منقضی» stay apart.
const digits = (value) => new Intl.NumberFormat("fa-IR").format(value);

export function remainingText(remaining) {
    if (!remaining) return "";
    if (remaining.days >= 1) return `${digits(remaining.days)} روز باقی‌مانده`;
    if (remaining.hours >= 1) return `${digits(remaining.hours)} ساعت باقی‌مانده`;
    return "کمتر از یک ساعت باقی‌مانده";
}

export function endDateText(iso) {
    if (!iso) return "";
    try {
        return new Intl.DateTimeFormat("fa-IR-u-ca-persian", {day: "numeric", month: "long", year: "numeric", timeZone: "Asia/Tehran"}).format(new Date(iso));
    } catch {
        return "";
    }
}

// a subscription whose end is close enough to count down (under a year)
export const hasCountdown = (status) => Boolean(status?.periodEnd && status?.remaining && status.remaining.days < 365);

// how much of the current period has passed, 0–100, or null without a start
export function elapsedPercent(status, now = Date.now()) {
    const start = Date.parse(status?.periodStart || "");
    const end = Date.parse(status?.periodEnd || "");
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
    return Math.max(0, Math.min(100, Math.round(((now - start) / (end - start)) * 100)));
}

const planOf = (status) => status?.planName || (status?.trial ? "دورهٔ آزمایشی" : "اشتراک");

// the short line in the sidebar: {text, tone, icon}
export function indicatorOf(status) {
    if (!status) return null;
    const plan = planOf(status);
    switch (status.state) {
        case "active":
            // the remaining time is always shown (boss's task «کاربران روز های باقی مانده اشتراک خود را بتوانند مشاهده
            // کنند», docs/tasks/40); only a far end date (a year or more, e.g. an unlimited account) is just «فعال»
            return hasCountdown(status)
                ? {text: `${plan} · ${remainingText(status.remaining)}`, tone: "ok", icon: "pi-check-circle"}
                : {text: `${plan} · فعال`, tone: "ok", icon: "pi-check-circle"};
        case "trial":
            return {text: `دورهٔ آزمایشی · ${remainingText(status.remaining)}`, tone: "info", icon: "pi-clock"};
        case "expiring":
        case "trial_expiring":
            return {text: `${status.trial ? "دورهٔ آزمایشی" : plan} · ${remainingText(status.remaining)}`, tone: status.level <= 3 ? "warn" : "info", icon: "pi-clock"};
        case "grace":
            return {text: "دورهٔ ارفاق · تمدید کنید", tone: "warn", icon: "pi-exclamation-triangle"};
        case "expired":
            return {text: status.trial ? "دورهٔ آزمایشی تمام شده" : "اشتراک منقضی شده", tone: "danger", icon: "pi-exclamation-circle"};
        case "payment_pending":
            return {text: "پرداخت در حال بررسی", tone: "info", icon: "pi-hourglass"};
        case "payment_failed":
            return {text: "پرداخت ناموفق", tone: "danger", icon: "pi-times-circle"};
        case "suspended":
            return {text: "اشتراک متوقف شده", tone: "danger", icon: "pi-ban"};
        case "none":
            return {text: "بدون اشتراک فعال · مشاهده پلن‌ها", tone: "neutral", icon: "pi-star"};
        default:
            return null;
    }
}

// the banner above the page, only when action is needed: {message, tone, action, dismissKey, critical} or null
export function bannerOf(status) {
    if (!status) return null;
    const ask = status.canManage ? null : "برای خرید یا تمدید با مدیر فضای کاری تماس بگیرید.";
    const renew = status.trial ? "انتخاب پلن" : "تمدید اشتراک";
    const end = endDateText(status.periodEnd);
    switch (status.state) {
        case "expiring":
        case "trial_expiring": {
            const left = status.remaining?.days >= 1 ? `${digits(status.remaining.days)} روز` : "کمتر از یک روز";
            const what = status.trial ? "دورهٔ آزمایشی" : "اشتراک";
            return {
                message: `${left} تا پایان ${what} باقی مانده است${end ? ` (${end})` : ""}. ${ask || "برای جلوگیری از وقفه در استفاده از امکانات، اشتراک خود را تمدید کنید."}`,
                tone: status.level <= 1 ? "error" : "warn",
                action: status.canManage ? renew : null,
                critical: status.level <= 1,
                dismissKey: `expiring:${status.periodEnd}:${status.level}`,
            };
        }
        case "grace":
            return {message: `اعتبار اشتراک گذشته و تا ${endDateText(status.graceEnd)} در دورهٔ ارفاق هستید. ${ask || "برای ادامهٔ کار، اشتراک را تمدید کنید."}`, tone: "error", action: status.canManage ? "تمدید اشتراک" : null, critical: true};
        case "expired":
            return {message: `${status.trial ? "دورهٔ آزمایشی" : "اشتراک"} این فضای کاری ${end ? `${end} ` : ""}منقضی شده است. ${ask || "برای ادامهٔ کار یک پلن انتخاب یا اشتراک را تمدید کنید."}`, tone: "error", action: status.canManage ? renew : null, critical: true};
        case "payment_pending":
            return {message: "پرداخت شما در حال بررسی است. پس از تأیید، وضعیت اشتراک به‌روز می‌شود.", tone: "info", action: status.canManage ? "مشاهدهٔ وضعیت پرداخت" : null, critical: false, dismissKey: `pending:${status.periodEnd || ""}`};
        case "payment_failed":
            return {message: `آخرین پرداخت ناموفق بود؛ وضعیت اشتراک تغییری نکرده است. ${ask || "می‌توانید دوباره پرداخت کنید."}`, tone: "error", action: status.canManage ? "تلاش دوباره" : null, critical: true};
        case "suspended":
            return {message: "اشتراک این فضای کاری متوقف شده است. با پشتیبانی زوفینو تماس بگیرید.", tone: "error", action: null, critical: true};
        default:
            return null;
    }
}
